// Building the payload a model judges, and validating what it returns.
//
// This module does NOT call a model. It produces a request and checks a
// response, so the package stays dependency-free and the judging stage is
// testable without a network. The caller — a backend agent, a CI job, an
// interactive session — owns the transport.
//
// Three design rules, each from a specific failure:
//
//   1. THE JUDGE SEES RAW INSTANCES. Handed "cluster #47 · 200 occurrences ·
//      verbs python3, jq" a model will produce a confident, plausible, wrong
//      name. This pipeline's worst call was dismissing a live reverse-
//      engineering programme from a count of 53; reading three of those
//      commands reversed it immediately. Aggregates are context, never
//      evidence.
//
//   2. FORCED CHOICE, NOT INDEPENDENT APPROVAL. Asked "is this worth a lens?"
//      one cluster at a time, a model says yes at a rate that makes the answer
//      meaningless — and at a thousand clusters that is a thousand lenses.
//      Scarcity has to be structural: rank a batch against itself and keep a
//      budget, rather than asking the judge to feel selective.
//
//   3. EVERY VERDICT NAMES A CONSUMER AND A FALSIFIER. "Who reads this and what
//      do they decide" is the difference between a lens and a data dump. A
//      stated falsifier is what makes a verdict reviewable later against
//      whether anyone actually used the thing.

import { redactPaths, SUB_LOOKUP } from "../mine/skeleton.js";
import { renderEvidence, pickInstances } from "./evidence.js";

export const VERDICT_SCHEMA = {
  type: "object",
  required: ["ranking", "verdicts"],
  properties: {
    ranking: {
      type: "array",
      description: "Cluster keys, most worth a lens first. Every key in the batch appears exactly once.",
      items: { type: "string" },
    },
    verdicts: {
      type: "array",
      items: {
        type: "object",
        required: ["key", "kind", "name", "summary", "consumer", "decision", "confidence", "falsifier"],
        properties: {
          key: { type: "string" },
          kind: {
            type: "string",
            enum: ["behaviour", "scaffolding", "already-covered", "too-varied"],
            description:
              "behaviour = a coherent act worth making legible. scaffolding = positioning work, not the work. "
              + "already-covered = an existing extractor recovers this. too-varied = the cluster is not one thing.",
          },
          name: { type: "string", description: "2-5 words, what an operator would call this act." },
          summary: { type: "string", description: "One sentence: what the agent is doing and to what." },
          consumer: { type: "string", description: "Who opens this lens." },
          decision: { type: "string", description: "What that person decides differently once they can see it." },
          fields: {
            type: "array",
            description: "The slots worth extracting, named. These become the extractor's fields.",
            items: { type: "string" },
          },
          target: {
            type: "string",
            description:
              "What the act operates ON, named concretely — a repository file, a dashboard, an issue, "
              + "a published URL, a temp file the agent just wrote, the session's own control flow.",
          },
          targetOutlivesSession: {
            type: "boolean",
            description:
              "Does that target exist independently of the session that touched it? A repo path, a "
              + "dashboard, an issue and a published artifact all do — a different session can touch the "
              + "same one. A scratch file the agent created minutes earlier, or the session's own retry "
              + "loop, does not.",
          },
          axis: {
            type: "string",
            enum: ["scope-tab", "trace-only"],
            description:
              "scope-tab = worth reading across MANY traces at once. trace-only = worth reading inline "
              + "beside the surrounding work, but not aggregated.",
          },
          axisReason: {
            type: "string",
            description:
              "Why that axis. For trace-only this ships as the lens' tabReason, so write it for a "
              + "reader who asks why this has no tab.",
          },
          coveredBy: { type: "string", description: "Existing extractor name, when kind is already-covered." },
          claimScope: {
            type: "string",
            enum: ["whole-cluster", "subset"],
            description:
              "Whether the behaviour you named is EVERY call this skeleton matches, or only the calls "
              + "whose values carry it. Check the slot lines before answering whole-cluster: a varied "
              + "slot under a specific name means subset.",
          },
          where: {
            type: "string",
            description:
              "For a subset claim: a case-insensitive regex over the raw command that matches the calls "
              + "your claim is about and no others. It is verified against the sampled instances and "
              + "ships in the lens, so the door claims only what you named.",
          },
          outcomePattern: {
            type: "string",
            description:
              "OPTIONAL, and the door for an OUTCOME-DEFINED behaviour: a case-insensitive regex over "
              + "what came BACK. Use when the act is identified by its result rather than its shape — "
              + "a path that did not resolve, an expired credential, a rate limit — including when the "
              + "same result spans several clusters. Verify it with search_outputs first; the lens "
              + "claims only calls whose output matches.",
          },
          checks: {
            type: "array",
            description:
              "Deterministic probes for the SEMANTICS your name and summary assert — one per claimed "
              + "property that is not readable off the skeleton itself. Each is executed against the "
              + "sampled evidence now (a probe matching nothing rejects the verdict) and re-executed by "
              + "the lens on every call it ever claims, so the claim stays checkable after you are gone. "
              + "A property you cannot write a probe for is a property you cannot claim — name the "
              + "probeable act instead.",
            items: {
              type: "object",
              required: ["name", "on", "pattern"],
              properties: {
                name: { type: "string", description: "What the probe proves, as a reader-facing phrase: 'writes the script', 'reports pass/fail counts'." },
                on: {
                  type: "string",
                  enum: ["command", "output"],
                  description:
                    "What the probe reads. `command` is the full raw command including any heredoc or -c "
                    + "script source; `output` is what came back. A claim about a RESULT must probe the output.",
                },
                pattern: { type: "string", description: "Case-insensitive regex, <=200 chars." },
              },
            },
          },
          confidence: { type: "string", enum: ["high", "medium", "low"] },
          falsifier: {
            type: "string",
            description: "What observation would show this verdict was wrong.",
          },
        },
      },
    },
  },
};

export const JUDGE_INSTRUCTIONS = `You are triaging clusters of agent tool calls to decide which represent
behaviours worth building a dedicated reading ("X-ray") for.

An X-ray turns raw tool calls into typed operations an operator can read: what
was acted on, what happened, how far it got. It is worth building when the raw
calls hide something a person would want to see, and not worth building when the
calls already say plainly what they are.

## The default answer is NO, and this section is why

Before anything else, run this test on every cluster, and expect it to refuse:

  IF A READER WHO KNOWS THE TOOLS WOULD LEARN THE SAME THING FROM THE RAW CALLS
  IN A MONOSPACE LIST, THE LENS IS TYPOGRAPHY, NOT A READING.

This is not a tiebreaker. It was applied retroactively to the nine lenses that
existed when it was written and it called all nine of them formatting. A pass
that returns a lens per cluster has not judged; it has relabelled the corpus.

\`grep -rn foo src/ | head\` fails the test outright. So does \`kubectl logs\`,
\`cat\`, \`ls\`, \`head\`, \`sed -n '1,50p' file\`, \`cargo test\`. Each is one verb and
one target, already legible; rendering it as
"search · src/ · answered" adds a font.

THE SHARPEST TELL IS IN YOUR OWN FIELD LIST. If the fields you would extract are
recoverable by mechanically parsing the command — its flags, its positionals, its
result limit, its search root — then the lens re-renders the command and adds
nothing. Fields like "search root", "file filter", "result limit", "matched
paths" are grep's arguments, not properties of a behaviour. When your field list
looks like that, the verdict is \`scaffolding\`.

What survives the test is work whose MEANING is not in the call: a value that had
to be read out of an earlier result before this call could be written, an outcome
the command does not state, a target assembled from several steps, a consequence
outside the sandbox. If you cannot point at something the raw list does not
already show, say \`scaffolding\` and move on. "Nothing here is worth building" is
a successful run.

Judge from the RAW COMMANDS AND WHAT CAME BACK. Both are shown, labelled \`sent\`
and \`got\`. The counts and metrics are context for weighing candidates against
each other; they are not evidence about what a cluster is. If the evidence and
the metrics disagree, the evidence wins.

THE RESULT IS USUALLY WHERE THE INTEREST IS. The same command returning three
healthy pods and returning CrashLoopBackOff has the same skeleton, the same
metrics, and is a different act. So a cluster whose commands look dull may still
be a behaviour, and one whose commands read impressively may return nothing at
all.

AND IT IS THE ONLY EVIDENCE FOR A FIELD. Every field you name must be something
you can POINT AT in a result above, or read directly off a command. A field you
expect a script to print — "completion status", "records changed", "stage
outcome" — is a guess about output you were shown, and if the output does not
contain it the lens renders an empty column. When the results are absent or say
nothing, the honest verdict is \`scaffolding\`, not a behaviour with imagined
fields. A filename is not evidence of what the program does.

## A verdict names every call the door will match, not only the ones you read

The skeleton is the DOOR the lens built from your verdict will match. Your name
and summary get stamped on every future call with this shape — not just the
sampled instances above. Each cluster's \`slot contents\` lines show what the
placeholders actually held across the WHOLE cluster; read them before naming,
because five samples cannot tell you how one shape varies and the slots can.

Every behaviour states its claim's scope, and \`subset\` is the common honest
answer:

  claimScope: whole-cluster — every call with this shape IS this behaviour.
    Claim it only when the shape alone implies the act: the program and flags
    leave nothing meaningful to vary, or the slot spread shows the values are
    one thing. \`promtool test rules <PATH.yaml>\` is whole-cluster; almost no
    grep is.
  claimScope: subset — the behaviour lives in specific VALUES, not the shape.
    \`grep -n <ARG> <PATH.md>\` is "conflict-marker finding" only when <ARG> is a
    conflict marker; the same skeleton carries every documentation search this
    corpus ever ran. Give \`where\`: a case-insensitive regex over the raw
    command matching the calls your claim is about and no others. It is checked
    against the evidence — a \`where\` matching none of the sampled calls rejects
    the verdict — and it ships in the lens, so the door claims only what you
    named.

NAME AT THE ALTITUDE OF THE MATCH. A name the door cannot honour is wrong even
when it fits the instances you happened to read. Without a discriminator,
"conflict-marker finding" is unsupportable for \`grep -n <ARG> <PATH.md>\`:
either supply the \`where\` that carves it, or name what the whole cluster is —
and notice that the honest broad name ("line-numbered file search") usually
fails the typography test above, which is the answer.

## Behaviours defined by their RESULT, not their shape

The clustering is syntactic: one shape per cluster. Some real behaviours are not
shaped at all — they are identified by what came back, and the same outcome
appears under several shapes at once. A path addressed from the wrong root fails
identically under sed, grep and cargo; an expired token fails identically under
curl and gh. Each cluster shows you a fragment; none of them shows you the
behaviour.

\`search_outputs\` searches every kept instance in the batch, across clusters, by
command or by output. Use it when an evidence result looks like a RECURRING
FAILURE MODE rather than this cluster's business, and when it comes back spanning
several keys, say so: name the behaviour for the outcome, give
\`outcomePattern\` (the result regex, which becomes the lens's door), and attach
the verdict to the cluster where it is densest. That verdict is worth more than
another grep-shaped reading, because no per-cluster reader can see it.

## Prove the name: every behaviour carries checks

A name asserts semantics, and every semantic property it asserts that is not
readable off the skeleton itself must decompose into a CHECK — a named probe
(\`on\`: command or output, \`pattern\`: regex) that is executed, twice: against
the sampled evidence now (a probe matching nothing rejects the verdict), and by
the built lens against every call it ever claims, so the claim stays checkable
long after this judgement.

  "edits the script"            → on: command, a write-shaped pattern over the
                                  inline source (the heredoc/-c text IS part of
                                  the raw command)
  "observes pass/fail counts"   → on: output, a pattern for the counts — a claim
                                  about a RESULT is only checkable in the output
  "runs the verifier"           → on: command, the script invocation

A CHECK MUST NOT REPEAT THE DOOR. If your only probe is the same pattern as
your \`where\` or \`outcomePattern\`, it passes on every claimed op by
construction and proves nothing — probe a property the door does not already
guarantee (what the act names, what it recovered, whether it reached a terminal
state).

Write the probe for what the evidence SHOWS, not for what the story needs. If a
property has no probe — "immediately", "iteratively", intent of any kind — you
cannot claim it; name the probeable act instead, and expect the renamed act to
face the typography test honestly. A trivially-true probe on a shape-obvious
act is fine; an unprobeable adjective is not.

PATTERNS ARE JAVASCRIPT, NOT PYTHON. Every \`where\` and check pattern executes
as \`new RegExp(pattern, "i")\`: inline flags like (?s)/(?i) do not exist ("i"
is already applied; use [\\s\\S] where python would use (?s) with a dot),
(?P<name>) is (?<name>), \\A/\\Z are ^/$. A pattern that does not compile there
rejects the verdict outright, however well it matched in another dialect.

TWO TELLS THAT YOUR SCOPE OR CHECKS ARE WRONG:
  - Your falsifier reads "other calls with this skeleton do X without Y". That
    sentence IS a subset admission — move Y into \`where\` or a check, or change
    claimScope to subset. A falsifier that contradicts your claimScope rejects
    the verdict in spirit even when the validator cannot parse prose.
  - The cluster has a \`<python src>\` / \`<node src>\` slot. Inline-script legs
    have no token slots, so that line is the ONLY spread evidence: its distinct
    count is how much the script itself varies. A claim about what the script
    DOES needs an on:command check over its text.

CONFIDENCE IS CALIBRATION, NOT EMPHASIS.
  high    every sampled instance shows the claimed act AND the scope is earned —
          whole-cluster with concentrated slots, or subset with a \`where\` that
          matches the evidence.
  medium  the instances support it, but the slots vary in ways the claim glosses
          over, or the results are thin.
  low     the claim rests on filenames, metric counts, or fewer than three
          informative results. A low-confidence behaviour is usually better
          returned as scaffolding.

Rank the batch against itself. Do not score clusters independently — decide
which are more worth building than which. Assume a budget of a handful.

## Clusters grouped under one FAMILY are variants of one behaviour

The payload groups clusters by the programs they run. Clusters in the same family
differ only in flags — \`grep -rn … | head\`, \`grep -rl … | head\`,
\`grep -n --include=… | head\` are one behaviour at three depths, not three
behaviours. The skeletonizer splits them because it will not make that judgement
for you; making it is your job.

So: AT MOST ONE \`behaviour\` PER FAMILY. Pick the variant whose evidence is
strongest, and mark every other cluster in that family \`already-covered\` with
\`coveredBy\` naming the key you picked. Three verdicts describing the same act in
different prose is the failure this rule exists to prevent — it produces three
lenses that match the same calls, and a reader cannot tell them apart.

For each cluster classify it as:
  behaviour        a coherent act worth making legible
  scaffolding      positioning work rather than the work itself
  already-covered  an existing extractor recovers this already
  too-varied       the cluster is not one thing; the skeleton over-merged

Prefer a behaviour where:
  - the act has consequence outside the sandbox, or produces a finding someone
    acts on
  - the raw form is genuinely hard to read (long, indirect, machine-written)
  - a reader would ask a follow-up question the raw calls cannot answer

Be sceptical where:
  - the command already reads as what it is (one verb, one target)
  - the cluster is dense in one session only and may be a single task, not a
    recurring behaviour
  - you cannot name who would open it

Every verdict must name a consumer, the decision that consumer makes, and a
falsifier — an observation that would show the verdict wrong. A verdict you
cannot falsify is a guess; mark those low confidence.

## Which axis it belongs on

A behaviour renders on one or both of two axes, and this is a separate judgement
from whether it is a behaviour at all. Both real mistakes made here so far were
axis mistakes on candidates that WERE genuine behaviours.

  scope-tab   one lens read across many traces at once, grouped
  trace-only  read inline, beside the work that surrounds it

The test is the TARGET. Name what the act operates on, then ask whether that
target exists independently of the session that touched it:

  - A repository file, a dashboard, a tracker issue, a fetched page, a published
    artifact: yes. A different session can act on the same one, so grouping
    accumulates evidence about a shared object and the group means something.
  - A scratch file the agent wrote minutes earlier, a temp log, the session's own
    retry loop or control flow: no. Grouping across sessions either yields
    singletons, or — worse — merges unrelated things that happen to share a name.

Two worked examples, both from real corrections:

  A retry loop waiting on a container is a real behaviour with a real reading
  ("it gave up after 120s and carried on"), but its subject is the session's own
  execution. Aggregated it becomes a mechanism tally — "sessions poll logs" —
  which nobody acts on. TRACE-ONLY.

  An agent re-reading a JSON file it wrote earlier is a real behaviour, and the
  file looks like a target. But a scratch file named "state.json" in one session
  and one named "state.json" in another are unrelated files, and a basename is
  often the only recoverable identity, so a tab would render forty unrelated
  reads as one artifact.
  TRACE-ONLY.

The operative property is IDENTIFIABILITY, not repetition. Measuring this on a
real corpus produced a result worth stating plainly: file paths — the archetypal
durable target — were touched by more than one session only 0-3% of the time
across 173 distinct paths. Yet a lens over file work is plainly worth a tab,
because a reader knows what "dashboard/src/lib/auth.js" IS without knowing which
session touched it. That is the test. A bare "state.json" fails it, because the
name does not identify which file, and a tab would merge unrelated ones.

So: a recurrence figure is supporting evidence when present, never a
requirement. What is required is that a reader could identify the target from
the value alone.

Default to trace-only anyway. A scope tab is the stronger claim, and if you
claim scope-tab for a target that does not outlive its session, the verdict is
rejected.`;

/**
 * The judging payload for a batch of clusters.
 *
 * @param {object[]} clusters
 * @param {object} opts
 *   instances  raw commands shown per cluster (default 5). The floor that
 *              matters: below ~3 the judge is guessing from one example.
 *   covered    known extractor domains, so "already-covered" is answerable.
 *   budget     how many the judge should treat as buildable.
 */
/**
 * The programs a cluster's skeleton runs, as a stable family key.
 *
 * Derived from the skeleton rather than stored on the cluster, so this needs no
 * change to the mining shape. Flags are dropped deliberately: `grep -rn` and
 * `grep -rl` differ by a flag and are the same behaviour, which is exactly the
 * split the judge was previously shown as two independent candidates.
 */
export function programFamily(cluster) {
  const stages = String(cluster?.skeleton || "").split("|");
  const progs = stages
    .map((st) => {
      const toks = st.trim().split(/\s+/).filter(Boolean);
      const prog = toks[0] || "";
      if (!prog || prog.startsWith("<")) return "";
      // A SUBCOMMAND-BEARING program's verb is part of its identity: `cargo
      // test` and `cargo clippy` are as different as `git log` and `git push`,
      // and folding them into one family forced the judge to mark one of two
      // genuinely different acts already-covered by the other. The subcommand
      // is the first bare word after the program, before any flag — the same
      // rule the consequence table uses.
      if (!SUB_LOOKUP.has(prog.toLowerCase())) return prog;
      const verb = toks.slice(1).find((t) => /^[a-z][\w-]{0,20}$/i.test(t) && !t.startsWith("<"));
      return verb ? `${prog} ${verb.toLowerCase()}` : prog;
    })
    .filter(Boolean);
  return [...new Set(progs)].sort().join("+") || "(unresolved)";
}

export function buildJudgeRequest(clusters, { instances = 5, covered = [], budget = 5, redact = true } = {}) {
  if (!Array.isArray(clusters) || !clusters.length) throw new TypeError("buildJudgeRequest needs clusters");
  if (instances < 3) throw new RangeError("instances must be >= 3: fewer than three examples is guessing");

  // Grouped by family so variants of one behaviour arrive together. Presented flat,
  // they read as independent candidates and get independent verdicts — which is how
  // three prose descriptions of one grep landed as three lenses.
  const families = new Map();
  for (const c of clusters) {
    const f = programFamily(c);
    if (!families.has(f)) families.set(f, []);
    families.get(f).push(c);
  }

  const render = (c, i) => {
    const m = [
      `${c.count} instances`,
      `${c.sessions} sessions`,
      `${c.endpoints} endpoints`,
      `${c.tenants} tenants`,
      `concentration ${c.concentration}/session`,
      // Tri-state. "read-only" is a CLAIM, so it is only made when the verb was
      // actually classified; an unclassified one says so rather than defaulting
      // to the reassuring answer. The judge is looking at the raw commands
      // directly below this line and can read the verb itself.
      c.consequence?.mutates === true ? "mutates state"
        : c.consequence?.mutates === false ? "read-only"
        : "consequence not classified",
      c.consequence?.egress ? "leaves the host" : null,
      c.embedded ? `embedded ${c.embedded}` : null,
    ].filter(Boolean).join(" · ");
    // The slot profile is the anti-extrapolation evidence: the sampled calls
    // below are a handful, and these lines are the WHOLE cluster's values. A
    // judge shown five conflict-marker greps and a slot line saying the pattern
    // slot held 34 distinct values knows the five were not the cluster.
    const slots = (c.slots || []).slice(0, 6).map((s) => {
      const top = (s.top || [])
        .map(({ value, n }) => `${JSON.stringify(redact ? redactPaths(value) : value)} ×${n}`)
        .join(" · ");
      return `  ${s.ph}: ${s.distinct}${s.truncated ? "+" : ""} distinct over ${s.total}${top ? ` · top: ${top}` : ""}`;
    });
    // Redaction is ON by default. The evidence is raw commands, so it carries
    // home-directory paths and therefore operator identities; a server-side
    // judge over someone else's traces must not receive those. Turn it off only
    // when the corpus is your own.
    // Rendered by the shared evidence module, so the two judging paths cannot
    // disagree about what a judge is shown. `pickInstances` spreads the sample
    // across sessions and prefers calls that HAVE a result — five examples from one
    // minute of one session is one example.
    return `### Cluster ${i + 1} — key: ${c.key}\n`
      + `skeleton: ${c.skeleton}\n`
      + `metrics (NOT evidence — weigh the calls below over these): ${m}\n`
      + (slots.length
        ? `slot contents (every call's values, not just the sample below — what the skeleton hides):\n${slots.join("\n")}\n`
        : "")
      + `\n${renderEvidence(pickInstances(c.instances, { want: instances }), { redact })}`;
  };

  let n = 0;
  const body = [...families.entries()].map(([family, members]) => {
    const head = members.length > 1
      ? `## Family \`${family}\` — ${members.length} variants of ONE behaviour. At most one may be a behaviour; mark the rest already-covered by the one you pick.\n`
      : `## Family \`${family}\`\n`;
    return head + members.map((c) => render(c, n++)).join("\n\n");
  }).join("\n\n");

  return {
    instructions: JUDGE_INSTRUCTIONS,
    schema: VERDICT_SCHEMA,
    prompt:
      `${clusters.length} candidate clusters. Rank them against each other and classify each.\n`
      + `Treat roughly ${budget} as buildable.\n`
      + (covered.length ? `\nExisting extractors already cover: ${covered.join(", ")}.\n` : "")
      + `\n${body}\n`,
    keys: clusters.map((c) => c.key),
  };
}

// A discriminator from a verdict is untrusted input with the same cap the lens
// compiler applies, so a pattern the lens would refuse cannot pass judging.
const MAX_WHERE = 200;
// Past this a claim is a grammar wearing a probe list; committed extractors are
// where grammars live.
const MAX_CHECKS = 6;

/**
 * How much of a cluster's kept evidence a probe actually matches.
 *
 * The measurement that keeps a semantic claim honest, exported because three
 * surfaces need the same number: validation rejects a probe that matches
 * nothing, the verdict card shows the split, and the draft's notes carry it to
 * the mount. `re: null` means the pattern itself was refused (invalid, or over
 * the cap the lens compiler shares).
 *
 * `on` picks the evidence: `command` reads the raw call (heredoc and -c source
 * included), `output` reads what came back — the only place a claim about a
 * RESULT can be checked. An instance with no captured output simply cannot
 * support an output probe, which is the honest arithmetic.
 */
export function probeSupport(cluster, { on = "command", pattern } = {}) {
  const src = String(pattern ?? "");
  const total = (cluster?.instances || []).length;
  if (!src || src.length > MAX_WHERE) return { re: null, matched: 0, total };
  let re;
  try {
    re = new RegExp(src, "i");
  } catch {
    return { re: null, matched: 0, total };
  }
  const matched = (cluster?.instances || [])
    .filter((i) => re.test(String((on === "output" ? i?.output : i?.command) || "")))
    .length;
  return { re, matched, total };
}

/**
 * The `where` discriminator's support — a command probe by definition.
 */
export function discriminatorSupport(cluster, where) {
  return probeSupport(cluster, { on: "command", pattern: where });
}

/**
 * Check a judge response before anything downstream trusts it. A malformed or
 * partial verdict set silently becomes a wrong build queue, so this rejects
 * rather than repairs.
 */
export function validateVerdicts(response, keys, { clusters } = {}) {
  const problems = [];
  const expected = new Set(keys);
  const ranking = Array.isArray(response?.ranking) ? response.ranking : [];
  const verdicts = Array.isArray(response?.verdicts) ? response.verdicts : [];

  if (ranking.length !== expected.size) {
    problems.push(`ranking has ${ranking.length} keys, expected ${expected.size}`);
  }
  for (const k of ranking) if (!expected.has(k)) problems.push(`ranking names unknown cluster ${k}`);
  if (new Set(ranking).size !== ranking.length) problems.push("ranking repeats a key");

  const judged = new Set();
  for (const v of verdicts) {
    if (!expected.has(v?.key)) { problems.push(`verdict for unknown cluster ${v?.key}`); continue; }
    judged.add(v.key);
    for (const field of ["kind", "name", "summary", "consumer", "decision", "confidence", "falsifier"]) {
      if (!v[field] || !String(v[field]).trim()) problems.push(`${v.key}: missing ${field}`);
    }
    if (v.kind === "already-covered" && !v.coveredBy) problems.push(`${v.key}: already-covered without coveredBy`);
    // A behaviour with no fields is not buildable — the extractor would have
    // nothing to populate.
    if (v.kind === "behaviour" && !(v.fields || []).length) problems.push(`${v.key}: behaviour with no fields`);

    // The axis is only a question for something being built.
    if (v.kind !== "behaviour") continue;
    for (const field of ["axis", "target", "axisReason"]) {
      if (!v[field] || !String(v[field]).trim()) problems.push(`${v.key}: behaviour missing ${field}`);
    }
    if (typeof v.targetOutlivesSession !== "boolean") {
      problems.push(`${v.key}: targetOutlivesSession must be stated — it decides the axis`);
    }
    // THE MECHANICAL GATE. A scope tab asserts the target recurs; a target that
    // dies with its session cannot. Both axis mistakes made here would have been
    // caught by exactly this: a retry loop and a scratch file, each claimed for a
    // tab it could not support.
    if (v.axis === "scope-tab" && v.targetOutlivesSession === false) {
      problems.push(`${v.key}: scope-tab claimed for a target that does not outlive its session`);
    }
    // And the metrics get a veto over the claim, since the judge is told to weigh
    // them as context: a behaviour seen in one session only has no cross-session
    // evidence to aggregate, whatever its target.
    const c = clusters?.get?.(v.key) || clusters?.[v.key];
    if (v.axis === "scope-tab" && c && c.sessions <= 1) {
      problems.push(`${v.key}: scope-tab claimed on a cluster confined to one session`);
    }
    // THE SCOPE GATE. A skeleton is a syntactic door, and a verdict's name gets
    // stamped on every call that walks through it — so the judge must say
    // whether the name covers the whole shape or only the values that carry it,
    // and a subset claim must arrive with the discriminator that carves it. This
    // is what stopped `grep -n <ARG> <PATH.md>` shipping as "conflict-marker
    // finding" over every documentation search sharing the shape.
    if (v.claimScope !== "whole-cluster" && v.claimScope !== "subset") {
      problems.push(`${v.key}: claimScope must be whole-cluster or subset — a behaviour claim without a stated scope stamps its name on every call the skeleton matches`);
    }
    if (v.claimScope === "subset" && !String(v.where || "").trim()) {
      problems.push(`${v.key}: a subset claim needs \`where\` — the pattern that carves the calls the claim is about; without it the lens door is the whole skeleton`);
    }
    if (String(v.outcomePattern || "").trim()) {
      const support = probeSupport(c, { on: "output", pattern: v.outcomePattern });
      if (!support.re) {
        problems.push(`${v.key}: \`outcomePattern\` is not a valid regex, or longer than ${MAX_WHERE} chars`);
      }
      // Deliberately NOT rejected for zero matches in THIS cluster: an
      // outcome-defined behaviour is attached to the cluster where it is
      // densest, and its evidence legitimately lives across the batch. The
      // pattern still has to compile, and the lens still measures it per op.
    }
    if (String(v.where || "").trim()) {
      const support = discriminatorSupport(c, v.where);
      if (!support.re) {
        problems.push(`${v.key}: \`where\` is not a valid regex, or longer than ${MAX_WHERE} chars`);
      } else if (c && support.total > 0 && support.matched === 0) {
        // A discriminator that matches none of the evidence describes commands
        // the judge imagined, and a lens built on it reads "found nothing"
        // forever while looking precise.
        problems.push(`${v.key}: \`where\` matches none of the cluster's ${support.total} sampled calls — the discriminator describes commands that are not in the evidence`);
      }
    }
    // THE PROOF OBLIGATION. A name asserts semantics; the checks are the
    // deterministic probes those semantics decompose into, and they are
    // EXECUTED — here against the sampled evidence, and by the lens against
    // every call it ever claims. "Patch-and-verify cycle" shipped over a door
    // that admits runs that patched nothing precisely because nothing forced
    // the name's verbs to be checkable.
    const checks = Array.isArray(v.checks) ? v.checks : [];
    if (!checks.length) {
      problems.push(`${v.key}: a behaviour carries at least one check — the probe for the act its name claims. A semantic you cannot probe is a name to lower.`);
    }
    if (checks.length > MAX_CHECKS) {
      problems.push(`${v.key}: ${checks.length} checks — more than ${MAX_CHECKS} is a grammar, not a claim; a behaviour that complex earns a committed extractor`);
    }
    for (const chk of checks.slice(0, MAX_CHECKS)) {
      const label = String(chk?.name || "").trim();
      if (!label) { problems.push(`${v.key}: every check needs a name — it labels the probe on every op the lens claims`); continue; }
      if (chk?.on !== "command" && chk?.on !== "output") {
        problems.push(`${v.key}: check "${label}" must say what it reads — \`on\` is command or output`);
        continue;
      }
      // A probe that IS the door proves nothing: it passed by construction on
      // every op the lens claims, so the ✓ carries no information and the
      // falsifier can never fire. Seen immediately on a real corpus — an
      // outcome-doored lens whose only check re-tested its own outcomePattern
      // rendered "✓ the read found nothing" on every row, forever.
      const doorPattern = String(v.outcomePattern || "").trim() || String(v.where || "").trim();
      if (doorPattern && String(chk.pattern || "").trim() === doorPattern) {
        problems.push(`${v.key}: check "${label}" repeats the door's own pattern — it passes on every claimed op by construction. Probe something the door does not already guarantee.`);
        continue;
      }
      const support = probeSupport(c, chk);
      if (!support.re) {
        problems.push(`${v.key}: check "${label}" pattern is not a valid regex, or longer than ${MAX_WHERE} chars`);
      } else if (c && support.total > 0 && support.matched === 0) {
        problems.push(`${v.key}: check "${label}" matches none of the cluster's ${support.total} sampled calls — the claimed semantic is not in the evidence shown`);
      }
    }
  }
  for (const k of expected) if (!judged.has(k)) problems.push(`no verdict for ${k}`);

  // ONE BEHAVIOUR PER FAMILY. The count gate above passes three verdicts for one
  // grep, because it counts approvals and never compares them — and three lenses
  // matching the same calls is worse than one, since a reader cannot tell which to
  // open. Mechanically checkable, so it is checked rather than asked for.
  if (clusters) {
    const at = (k) => clusters?.get?.(k) || clusters?.[k];
    const byFamily = new Map();
    for (const v of verdicts) {
      if (v?.kind !== "behaviour") continue;
      const c = at(v.key);
      if (!c) continue;
      const f = programFamily(c);
      if (!byFamily.has(f)) byFamily.set(f, []);
      byFamily.get(f).push(v);
    }
    for (const [family, vs] of byFamily) {
      if (vs.length < 2) continue;
      problems.push(
        `${vs.length} behaviours claimed for one program family \`${family}\` `
        + `(${vs.map((v) => `"${v.name}"`).join(", ")}) — these run the same programs and differ only in flags. `
        + "Pick the strongest and mark the rest already-covered by it.",
      );
    }
  }

  return { ok: problems.length === 0, problems };
}
